import { useState, useRef, useCallback } from 'react';
import * as Cesium from 'cesium';
import { exportJsonFile } from '../utils/fileExportUtils';
import {
  createMeasurePoint,
  createMeasureLine,
  createMeasureLabel,
  calculateDistance,
  calculateHeight,
  calculateAngle,
  calculateSurfaceArea,
  formatMeasurement,
  getCenterPoint
} from '../utils/cesiumMeasureTools';
import {
  createTreeEntities,
  parseTreesFromGeoJson,
  TREE_CYLINDER_HEIGHT,
  TREE_HEIGHT_OFFSET,
  DEFAULT_TREE_COLOR,
  buildTreeGeoJsonFromEntities,
  downloadGeoJson
} from '../utils/treeCylinders';

/**
 * Measurement modes available in the viewer
 */
export const MEASUREMENT_MODES = {
  NONE: 'none',
  DISTANCE: 'distance',
  HEIGHT: 'height',
  ANGLE: 'angle',
  AREA: 'area',
  CIRCLE: 'circle',
  TREES: 'trees'
};

const TREE_COLOR = DEFAULT_TREE_COLOR;
const TREE_HIGHLIGHT_COLOR = Cesium.Color.ORANGERED.withAlpha(0.8);

/**
 * Custom hook for managing 3D measurements in Cesium viewer
 * Handles different measurement modes and their state
 */
export const useMeasurement = (viewer) => {
  // Current measurement mode
  const [measurementMode, setMeasurementMode] = useState(MEASUREMENT_MODES.NONE);
  
  // Points collected for current measurement
  const [measurementPoints, setMeasurementPoints] = useState([]);
  
  // Completed measurements storage
  const [completedMeasurements, setCompletedMeasurements] = useState([]);
  
  // Highlighted measurement state
  const [highlightedMeasurement, setHighlightedMeasurement] = useState(null);
  
  
  // Refs for accessing latest state in event handlers
  const measurementModeRef = useRef(measurementMode);
  const measurementPointsRef = useRef(measurementPoints);
  
  // Update refs when state changes
  measurementModeRef.current = measurementMode;
  measurementPointsRef.current = measurementPoints;

  /**
   * Clears all measurements and resets state
   */
  const clearMeasurements = useCallback(() => {
    if (viewer?.entities) {
      viewer.entities.removeAll();
    }
    setMeasurementPoints([]);
    setCompletedMeasurements([]);
  }, [viewer]);

  /**
   * Deletes a specific measurement by ID
   * @param {string} measurementId - ID of the measurement to delete
   */
  const deleteMeasurement = useCallback((measurementId) => {
    const viewer_ = viewer;
    if (!viewer_) return;

    // Find the measurement to delete
    const measurementToDelete = completedMeasurements.find(m => m.id === measurementId);
    if (!measurementToDelete) return;

    // Remove all entities associated with this measurement
    measurementToDelete.entities.forEach(entity => {
      if (viewer_.entities.contains(entity)) {
        viewer_.entities.remove(entity);
      }
    });

    // Remove from completed measurements list
    setCompletedMeasurements(prev => prev.filter(m => m.id !== measurementId));
  }, [viewer, completedMeasurements]);

  /**
   * Exports all completed measurements as a JSON file
   */
  const exportMeasurements = useCallback(() => {
    const forestMeasurements = completedMeasurements.filter(m => m.type === 'Forest report');
    const standardMeasurements = completedMeasurements.filter(m => m.type !== 'Forest report');

    if (!forestMeasurements.length && !standardMeasurements.length) {
      console.warn('No measurements to export');
      return;
    }

    if (standardMeasurements.length) {
      const exportData = {
        metadata: {
          exportDate: new Date().toISOString(),
          totalMeasurements: standardMeasurements.length,
          version: '1.0',
          source: 'Mirukuru Mapping Viewer'
        },
        measurements: standardMeasurements.map(measurement => ({
          type: measurement.type,
          value: measurement.value,
          coordinates: measurement.coordinates
        }))
      };

      exportJsonFile(`measurements_${new Date().toISOString().split('T')[0]}.json`, exportData);
    }

    forestMeasurements.forEach((measurement, index) => {
      const geojson = buildTreeGeoJsonFromEntities(measurement.entities, viewer);
      const filename = `${measurement.id || index}.geojson`;
      const ok = downloadGeoJson(geojson, filename);
      if (!ok) {
        console.warn('No GeoJSON data available to export for forest measurement', measurement.id);
      }
    });
  }, [completedMeasurements, viewer, buildTreeGeoJsonFromEntities, downloadGeoJson]);

  /**
   * Imports measurements from a JSON file
   * @param {File} file - The JSON file to import
   */
  const importMeasurements = useCallback(async (file) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);

      const viewer_ = viewer;
      if (!viewer_) {
        throw new Error('Viewer not initialized');
      }

      const isGeoJson = data?.type === 'FeatureCollection' && Array.isArray(data.features);

      if (isGeoJson) {
        const treeData = parseTreesFromGeoJson(data);

        if (treeData.length === 0) {
          throw new Error('No valid trees found in GeoJSON');
        }

        const treeEntities = createTreeEntities(viewer_, treeData, {
          color: TREE_COLOR,
          cylinderHeight: TREE_CYLINDER_HEIGHT,
          heightOffset: TREE_HEIGHT_OFFSET
        });

        const treeCount = treeEntities.length;
        const forestMeasurement = {
          id: `forest_report_${Date.now()}`,
          type: 'Forest report',
          value: treeCount,
          unit: 'trees',
          displayText: `${treeCount} ${treeCount === 1 ? 'tree' : 'trees'}`,
          entities: treeEntities,
          visible: true,
          timestamp: new Date(),
          coordinates: {
            trees: treeData.map(tree => ({
              id: tree.id,
              lon: tree.lon,
              lat: tree.lat,
              elevation: tree.elevation,
              radius: tree.radius,
              height: tree.treeHeight,
              direction: tree.direction ? { x: tree.direction.x, y: tree.direction.y, z: tree.direction.z } : null
            }))
          }
        };

        setCompletedMeasurements(prev => [...prev, forestMeasurement]);
        console.log(`Successfully imported ${treeCount} trees`);
        return;
      }
      
      if (!data.measurements || !Array.isArray(data.measurements)) {
        throw new Error('Invalid file format: measurements array not found');
      }

      const importedMeasurements = [];

      for (const measurement of data.measurements) {
        const { type, value, coordinates } = measurement;
        
        // Generate new ID and timestamp for imported measurement
        const measurementId = `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const entities = [];

        // Reconstruct measurement entities based on type
        switch (type) {
          case 'distance':
            const { point1, point2 } = coordinates;
            const p1 = new Cesium.Cartesian3(point1.x, point1.y, point1.z);
            const p2 = new Cesium.Cartesian3(point2.x, point2.y, point2.z);
            
            entities.push(
              viewer_.entities.add(createMeasurePoint(p1, Cesium.Color.RED)),
              viewer_.entities.add(createMeasurePoint(p2, Cesium.Color.RED)),
              viewer_.entities.add(createMeasureLine([p1, p2], Cesium.Color.RED)),
              viewer_.entities.add(createMeasureLabel(
                getCenterPoint([p1, p2]),
                formatMeasurement(value, 'm')
              ))
            );
            break;

          case 'height':
            const { clickPoint, groundPoint } = coordinates;
            const cp = new Cesium.Cartesian3(clickPoint.x, clickPoint.y, clickPoint.z);
            const gp = new Cesium.Cartesian3(groundPoint.x, groundPoint.y, groundPoint.z);
            
            entities.push(
              viewer_.entities.add(createMeasurePoint(cp, Cesium.Color.RED)),
              viewer_.entities.add(createMeasurePoint(gp, Cesium.Color.RED)),
              viewer_.entities.add(createMeasureLine([cp, gp], Cesium.Color.RED)),
              viewer_.entities.add(createMeasureLabel(cp, formatMeasurement(value, 'm')))
            );
            break;

          case 'angle':
            const { point1: ap1, vertex, point3: ap3 } = coordinates;
            const angle1 = new Cesium.Cartesian3(ap1.x, ap1.y, ap1.z);
            const angleVertex = new Cesium.Cartesian3(vertex.x, vertex.y, vertex.z);
            const angle3 = new Cesium.Cartesian3(ap3.x, ap3.y, ap3.z);
            
            entities.push(
              viewer_.entities.add(createMeasurePoint(angle1, Cesium.Color.RED)),
              viewer_.entities.add(createMeasurePoint(angleVertex, Cesium.Color.RED)),
              viewer_.entities.add(createMeasurePoint(angle3, Cesium.Color.RED)),
              viewer_.entities.add(createMeasureLine([angleVertex, angle1], Cesium.Color.RED)),
              viewer_.entities.add(createMeasureLine([angleVertex, angle3], Cesium.Color.RED)),
              viewer_.entities.add(createMeasureLabel(
                angleVertex,
                formatMeasurement(value, '°', 1),
                Cesium.Color.WHITE,
                Cesium.Color.BLACK
              ))
            );
            break;

          case 'area':
            const { points } = coordinates;
            const areaPoints = points.map(p => new Cesium.Cartesian3(p.x, p.y, p.z));
            
            // Add points
            areaPoints.forEach(point => {
              entities.push(viewer_.entities.add(createMeasurePoint(point, Cesium.Color.RED)));
            });
            
            // Add lines between consecutive points
            for (let i = 0; i < areaPoints.length; i++) {
              const nextIndex = (i + 1) % areaPoints.length;
              entities.push(viewer_.entities.add(createMeasureLine([areaPoints[i], areaPoints[nextIndex]], Cesium.Color.RED)));
            }
            
            // Add polygon
            entities.push(viewer_.entities.add({
              polygon: {
                hierarchy: areaPoints,
                fill: true,
                perPositionHeight: true,
                material: Cesium.Color.RED.withAlpha(0.3),
                outline: false,
                height: 0
              }
            }));
            
            // Add label
            entities.push(viewer_.entities.add(createMeasureLabel(
              getCenterPoint(areaPoints),
              formatMeasurement(value, 'm²'),
              Cesium.Color.WHITE,
              Cesium.Color.BLACK
            )));
            break;

          case 'circle':
            const { point1: cp1, point2: cp2, point3: cp3, center } = coordinates;
            const circle1 = new Cesium.Cartesian3(cp1.x, cp1.y, cp1.z);
            const circle2 = new Cesium.Cartesian3(cp2.x, cp2.y, cp2.z);
            const circle3 = new Cesium.Cartesian3(cp3.x, cp3.y, cp3.z);
            const circleCenter = new Cesium.Cartesian3(center.x, center.y, center.z);
            
            // Calculate radius and area from imported values
            const radius = typeof value === 'object' ? value.radius : Math.sqrt(value / Math.PI);
            const area = typeof value === 'object' ? value.area : value;
            
            // Add defining points
            entities.push(
              viewer_.entities.add(createMeasurePoint(circle1, Cesium.Color.RED)),
              viewer_.entities.add(createMeasurePoint(circle2, Cesium.Color.RED)),
              viewer_.entities.add(createMeasurePoint(circle3, Cesium.Color.RED)),
              viewer_.entities.add(createMeasurePoint(circleCenter, Cesium.Color.RED))
            );
            
            // Recreate circle geometry using the same 3D algorithm as creation
            // Calculate normal vector from the original 3 points
            const v1 = Cesium.Cartesian3.subtract(circle2, circle1, new Cesium.Cartesian3());
            const v2 = Cesium.Cartesian3.subtract(circle3, circle1, new Cesium.Cartesian3());
            const normal = Cesium.Cartesian3.cross(v1, v2, new Cesium.Cartesian3());
            Cesium.Cartesian3.normalize(normal, normal);
            
            // Create two perpendicular vectors in the circle's plane
            const vector1 = Cesium.Cartesian3.subtract(circle1, circleCenter, new Cesium.Cartesian3());
            const right = Cesium.Cartesian3.normalize(vector1, new Cesium.Cartesian3());
            const forward = Cesium.Cartesian3.cross(normal, right, new Cesium.Cartesian3());
            Cesium.Cartesian3.normalize(forward, forward);
            
            // Scale the basis vectors by the radius
            const rightScaled = Cesium.Cartesian3.multiplyByScalar(right, radius, new Cesium.Cartesian3());
            const forwardScaled = Cesium.Cartesian3.multiplyByScalar(forward, radius, new Cesium.Cartesian3());
            
            // Create circle points in 3D space
            const circlePoints = [];
            const numPoints = 64;
            for (let i = 0; i < numPoints; i++) {
              const angle = (i / numPoints) * 2 * Math.PI;
              const cosAngle = Math.cos(angle);
              const sinAngle = Math.sin(angle);
              
              // Calculate point on circle
              const circleOffset = Cesium.Cartesian3.add(
                Cesium.Cartesian3.multiplyByScalar(rightScaled, cosAngle, new Cesium.Cartesian3()),
                Cesium.Cartesian3.multiplyByScalar(forwardScaled, sinAngle, new Cesium.Cartesian3()),
                new Cesium.Cartesian3()
              );
              
              const circlePoint = Cesium.Cartesian3.add(circleCenter, circleOffset, new Cesium.Cartesian3());
              circlePoints.push(circlePoint);
            }
            
            entities.push(viewer_.entities.add({
              polygon: {
                hierarchy: circlePoints,
                fill: true,
                perPositionHeight: true,
                material: Cesium.Color.RED.withAlpha(0.3),
                outline: true,
                outlineColor: Cesium.Color.RED,
                outlineWidth: 3,
                height: 0
              }
            }));
            
            // Add label
            entities.push(viewer_.entities.add(createMeasureLabel(
              circleCenter,
              `R: ${formatMeasurement(radius, 'm')}\nArea: ${formatMeasurement(area, 'm²')}`,
              Cesium.Color.WHITE,
              Cesium.Color.BLACK
            )));
            break;

          default:
            console.warn(`Unknown measurement type: ${type}`);
            continue;
        }

        // Create completed measurement object
        const completedMeasurement = {
          id: measurementId,
          type,
          value,
          unit: type === 'distance' || type === 'height' ? 'm' : 
                type === 'angle' ? '°' : 
                type === 'area' ? 'm²' : 'm/m²',
          displayText: type === 'circle' && typeof value === 'object' ? 
            `R: ${formatMeasurement(value.radius, 'm')}, Area: ${formatMeasurement(value.area, 'm²')}` :
            formatMeasurement(value, type === 'distance' || type === 'height' ? 'm' : 
                              type === 'angle' ? '°' : 'm²'),
          entities,
          visible: true,
          timestamp: new Date(),
          coordinates
        };

        importedMeasurements.push(completedMeasurement);
      }

      // Add all imported measurements to the state
      setCompletedMeasurements(prev => [...prev, ...importedMeasurements]);
      
      console.log(`Successfully imported ${importedMeasurements.length} measurements`);
      
    } catch (error) {
      console.error('Error importing measurements:', error);
      throw error;
    }
  }, [viewer]);

  /**
   * Sets the measurement mode without clearing existing measurements
   * @param {string} mode - New measurement mode
   */
  const setMode = useCallback((mode) => {
    setMeasurementMode(mode);
    // Reset current measurement points but keep existing measurements
    setMeasurementPoints([]);
  }, []);

  /**
   * Handles distance measurement (2 points)
   * @param {Cesium.Cartesian3} position - Clicked position
   */
  const handleDistanceMeasurement = useCallback((position) => {
    const viewer_ = viewer;
    const points = [...measurementPointsRef.current, position];
    
    // Add point marker (red)
    const pointEntity = viewer_.entities.add(createMeasurePoint(position, Cesium.Color.RED));
    
    if (points.length === 2) {
      const [point1, point2] = points;
      const distance = calculateDistance(point1, point2);
      const midpoint = getCenterPoint([point1, point2]);
      
      // Add line and label (red)
      const lineEntity = viewer_.entities.add(createMeasureLine([point1, point2], Cesium.Color.RED));
      const labelEntity = viewer_.entities.add(createMeasureLabel(
        midpoint,
        formatMeasurement(distance, 'm')
      ));
      
      // Get first point entity (stored in temporary array)
      const firstPointEntity = viewer_._distanceFirstPoint;
      const allEntities = firstPointEntity ? [firstPointEntity, pointEntity, lineEntity, labelEntity] : [pointEntity, lineEntity, labelEntity];
      
      // Store completed measurement
      const measurementId = `distance_${Date.now()}`;
      const completedMeasurement = {
        id: measurementId,
        type: 'distance',
        value: distance,
        unit: 'm',
        displayText: formatMeasurement(distance, 'm'),
        entities: allEntities,
        visible: true,
        timestamp: new Date(),
        coordinates: {
          point1: { x: point1.x, y: point1.y, z: point1.z },
          point2: { x: point2.x, y: point2.y, z: point2.z }
        }
      };
      
      setCompletedMeasurements(prev => [...prev, completedMeasurement]);
      
      // Clear temporary storage
      viewer_._distanceFirstPoint = null;
      
      // Reset points for next measurement but don't clear existing measurements
      setMeasurementPoints([]);
    } else {
      // Store first point entity for later
      viewer_._distanceFirstPoint = pointEntity;
      setMeasurementPoints(points);
    }
  }, [viewer]);

  /**
   * Handles height measurement (1 point to ground)
   * @param {Cesium.Cartesian3} position - Clicked position
   */
  const handleHeightMeasurement = useCallback((position) => {
    const viewer_ = viewer;
    const heightData = calculateHeight(viewer_, position);
    
    // Add point markers (red)
    const pointEntity1 = viewer_.entities.add(createMeasurePoint(position, Cesium.Color.RED));
    const pointEntity2 = viewer_.entities.add(createMeasurePoint(heightData.groundPosition, Cesium.Color.RED));
    
    // Add vertical line and label (red)
    const lineEntity = viewer_.entities.add(createMeasureLine([position, heightData.groundPosition], Cesium.Color.RED));
    
    const labelEntity = viewer_.entities.add(createMeasureLabel(
      position,
      formatMeasurement(heightData.height, 'm')
    ));
    
    // Store completed measurement
    const measurementId = `height_${Date.now()}`;
    const completedMeasurement = {
      id: measurementId,
      type: 'height',
      value: heightData.height,
      unit: 'm',
      displayText: formatMeasurement(heightData.height, 'm'),
      entities: [pointEntity1, pointEntity2, lineEntity, labelEntity],
      visible: true,
      timestamp: new Date(),
      coordinates: {
        clickPoint: { x: position.x, y: position.y, z: position.z },
        groundPoint: { x: heightData.groundPosition.x, y: heightData.groundPosition.y, z: heightData.groundPosition.z }
      }
    };
    
    setCompletedMeasurements(prev => [...prev, completedMeasurement]);
  }, [viewer]);

  /**
   * Handles angle measurement (3 points)
   * @param {Cesium.Cartesian3} position - Clicked position
   */
  const handleAngleMeasurement = useCallback((position) => {
    const viewer_ = viewer;
    const points = [...measurementPointsRef.current, position];
    
    // Add point marker (red)
    const pointEntity = viewer_.entities.add(createMeasurePoint(position, Cesium.Color.RED));
    
    if (points.length === 3) {
      const [point1, vertex, point3] = points;
      const angle = calculateAngle(point1, vertex, point3);
      
      // Add lines from vertex to other points (red)
      const lineEntity1 = viewer_.entities.add(createMeasureLine([vertex, point1], Cesium.Color.RED));
      const lineEntity2 = viewer_.entities.add(createMeasureLine([vertex, point3], Cesium.Color.RED));
      
      // Add angle label at vertex
      const labelEntity = viewer_.entities.add(createMeasureLabel(
        vertex,
        formatMeasurement(angle.degrees, '°', 1),
        Cesium.Color.WHITE,
        Cesium.Color.BLACK
      ));
      
      // Get all point entities
      const allPointEntities = viewer_._anglePointEntities || [];
      const allEntities = [...allPointEntities, pointEntity, lineEntity1, lineEntity2, labelEntity];
      
      // Store completed measurement
      const measurementId = `angle_${Date.now()}`;
      const completedMeasurement = {
        id: measurementId,
        type: 'angle',
        value: angle.degrees,
        unit: '°',
        displayText: formatMeasurement(angle.degrees, '°', 1),
        entities: allEntities,
        visible: true,
        timestamp: new Date(),
        coordinates: {
          point1: { x: point1.x, y: point1.y, z: point1.z },
          vertex: { x: vertex.x, y: vertex.y, z: vertex.z },
          point3: { x: point3.x, y: point3.y, z: point3.z }
        }
      };
      
      setCompletedMeasurements(prev => [...prev, completedMeasurement]);
      
      // Clear temporary storage
      viewer_._anglePointEntities = [];
      
      // Reset points for next measurement but don't clear existing measurements
      setMeasurementPoints([]);
    } else {
      // Store point entity for later tracking
      if (!viewer_._anglePointEntities) viewer_._anglePointEntities = [];
      viewer_._anglePointEntities.push(pointEntity);
      setMeasurementPoints(points);
    }
  }, [viewer]);

  /**
   * Handles area measurement (multiple points, minimum 3)
   * Left click adds points, right click closes polygon
   * @param {Cesium.Cartesian3} position - Clicked position
   */
  const handleAreaMeasurement = useCallback((position) => {
    const viewer_ = viewer;
    const points = [...measurementPointsRef.current, position];
    
    // Add point marker (red)
    const pointEntity = viewer_.entities.add(createMeasurePoint(position, Cesium.Color.RED));
    
    // Store point entity in measurement points for later tracking
    const updatedPoints = [...measurementPointsRef.current, position];
    
    // Draw line to previous point if there is one
    if (points.length > 1) {
      const prevPoint = points[points.length - 2];
      const lineEntity = viewer_.entities.add(createMeasureLine([prevPoint, position], Cesium.Color.RED));
      // Add line entity to a temporary storage that we'll handle in closeAreaMeasurement
      if (!viewer_._areaLineEntities) viewer_._areaLineEntities = [];
      viewer_._areaLineEntities.push(lineEntity);
    }
    
    // Store point entity for later tracking
    if (!viewer_._areaPointEntities) viewer_._areaPointEntities = [];
    viewer_._areaPointEntities.push(pointEntity);
    
    setMeasurementPoints(updatedPoints);
  }, [viewer]);

  /**
   * Calculate circle center, radius, and normal from 3 points in 3D space
   * @param {Cesium.Cartesian3} p1 - First point
   * @param {Cesium.Cartesian3} p2 - Second point  
   * @param {Cesium.Cartesian3} p3 - Third point
   * @returns {Object|null} - {center, radius, normal} or null if points are collinear
   */
  const calculateCircleFrom3Points = useCallback((p1, p2, p3) => {
    // Calculate vectors from p1 to p2 and p1 to p3
    const v1 = Cesium.Cartesian3.subtract(p2, p1, new Cesium.Cartesian3());
    const v2 = Cesium.Cartesian3.subtract(p3, p1, new Cesium.Cartesian3());
    
    // Calculate normal to the plane containing the 3 points
    const normal = Cesium.Cartesian3.cross(v1, v2, new Cesium.Cartesian3());
    const normalMagnitude = Cesium.Cartesian3.magnitude(normal);
    
    // Check if points are collinear
    if (normalMagnitude < 1e-10) {
      return null;
    }
    
    Cesium.Cartesian3.normalize(normal, normal);
    
    // Use the circumcenter formula for 3D points
    // First, translate points so p1 is at origin
    const a = Cesium.Cartesian3.subtract(p2, p1, new Cesium.Cartesian3());
    const b = Cesium.Cartesian3.subtract(p3, p1, new Cesium.Cartesian3());
    
    // Calculate squared magnitudes
    const aMag2 = Cesium.Cartesian3.dot(a, a);
    const bMag2 = Cesium.Cartesian3.dot(b, b);
    const abDot = Cesium.Cartesian3.dot(a, b);
    
    // Calculate denominator for circumcenter formula
    const denominator = 2 * (aMag2 * bMag2 - abDot * abDot);
    
    if (Math.abs(denominator) < 1e-10) {
      return null; // Degenerate case
    }
    
    // Calculate circumcenter relative to p1
    const alpha = bMag2 * (aMag2 - abDot) / denominator;
    const beta = aMag2 * (bMag2 - abDot) / denominator;
    
    const centerRelative = Cesium.Cartesian3.add(
      Cesium.Cartesian3.multiplyByScalar(a, alpha, new Cesium.Cartesian3()),
      Cesium.Cartesian3.multiplyByScalar(b, beta, new Cesium.Cartesian3()),
      new Cesium.Cartesian3()
    );
    
    // Translate back to world coordinates
    const center = Cesium.Cartesian3.add(p1, centerRelative, new Cesium.Cartesian3());
    
    // Calculate radius (should be same distance to all 3 points)
    const radius = Cesium.Cartesian3.distance(center, p1);
    
    // Verify the calculation by checking distances to other points
    const radius2 = Cesium.Cartesian3.distance(center, p2);
    const radius3 = Cesium.Cartesian3.distance(center, p3);
    
    // Check if all radii are approximately equal (within 1cm tolerance)
    if (Math.abs(radius - radius2) > 0.01 || Math.abs(radius - radius3) > 0.01) {
      console.warn('Circle calculation error - unequal radii:', radius, radius2, radius3);
    }
    
    return { center, radius, normal };
  }, []);

  /**
   * Handles circle measurement (3 points defining the circle)
   * @param {Cesium.Cartesian3} position - Clicked position
   */
  const handleCircleMeasurement = useCallback((position) => {
    const viewer_ = viewer;
    const points = [...measurementPointsRef.current, position];
    
    // Add point marker (red)
    const pointEntity = viewer_.entities.add(createMeasurePoint(position, Cesium.Color.RED));
    
    if (points.length === 3) {
      const [point1, point2, point3] = points;
      
      // Calculate circle center and radius from 3 points
      const circleData = calculateCircleFrom3Points(point1, point2, point3);
      
      if (circleData) {
        const { center, radius, normal } = circleData;
        const area = Math.PI * radius * radius;
        
        // Create circle as polygon with many points in 3D space
        const circlePoints = [];
        const numPoints = 64; // Number of points to create the circle
        
        // Create two perpendicular vectors in the circle's plane
        const vector1 = Cesium.Cartesian3.subtract(point1, center, new Cesium.Cartesian3());
        const right = Cesium.Cartesian3.normalize(vector1, new Cesium.Cartesian3());
        const forward = Cesium.Cartesian3.cross(normal, right, new Cesium.Cartesian3());
        Cesium.Cartesian3.normalize(forward, forward);
        
        // Scale the basis vectors by the radius
        const rightScaled = Cesium.Cartesian3.multiplyByScalar(right, radius, new Cesium.Cartesian3());
        const forwardScaled = Cesium.Cartesian3.multiplyByScalar(forward, radius, new Cesium.Cartesian3());
        
        // Create circle points
        for (let i = 0; i < numPoints; i++) {
          const angle = (i / numPoints) * 2 * Math.PI;
          const cosAngle = Math.cos(angle);
          const sinAngle = Math.sin(angle);
          
          // Calculate point on circle
          const circleOffset = Cesium.Cartesian3.add(
            Cesium.Cartesian3.multiplyByScalar(rightScaled, cosAngle, new Cesium.Cartesian3()),
            Cesium.Cartesian3.multiplyByScalar(forwardScaled, sinAngle, new Cesium.Cartesian3()),
            new Cesium.Cartesian3()
          );
          
          const circlePoint = Cesium.Cartesian3.add(center, circleOffset, new Cesium.Cartesian3());
          circlePoints.push(circlePoint);
        }
        
        // Add circle as polygon
        const circleEntity = viewer_.entities.add({
          polygon: {
            hierarchy: circlePoints,
            fill: true,
            perPositionHeight: true,
            material: Cesium.Color.RED.withAlpha(0.3),
            outline: true,
            outlineColor: Cesium.Color.RED,
            outlineWidth:3,
            height: 0
          }
        });
        
        // Add center point
        const centerEntity = viewer_.entities.add(createMeasurePoint(center, Cesium.Color.RED));
        
        // Add labels
        const labelEntity = viewer_.entities.add(createMeasureLabel(
          center,
          `R: ${formatMeasurement(radius, 'm')}\nArea: ${formatMeasurement(area, 'm²')}`,
          Cesium.Color.WHITE,
          Cesium.Color.BLACK
        ));
        
        // Get all point entities
        const allPointEntities = viewer_._circlePointEntities || [];
        const allEntities = [...allPointEntities, pointEntity, circleEntity, centerEntity, labelEntity];
        
        // Store completed measurement
        const measurementId = `circle_${Date.now()}`;
        const completedMeasurement = {
          id: measurementId,
          type: 'circle',
          value: { radius, area },
          unit: 'm/m²',
          displayText: `R: ${formatMeasurement(radius, 'm')}, Area: ${formatMeasurement(area, 'm²')}`,
          entities: allEntities,
          visible: true,
          timestamp: new Date(),
          coordinates: {
            point1: { x: point1.x, y: point1.y, z: point1.z },
            point2: { x: point2.x, y: point2.y, z: point2.z },
            point3: { x: point3.x, y: point3.y, z: point3.z },
            center: { x: center.x, y: center.y, z: center.z }
          }
        };
        
        setCompletedMeasurements(prev => [...prev, completedMeasurement]);
        
        // Clear temporary storage
        viewer_._circlePointEntities = [];
        
        setMeasurementPoints([]);
      } else {
        // Points are collinear, can't form a circle
        viewer_.entities.add(createMeasureLabel(
          position,
          'Points are collinear - cannot form circle',
          Cesium.Color.RED,
          Cesium.Color.WHITE
        ));
        setMeasurementPoints([]);
      }
    } else {
      // Store point entity for later tracking
      if (!viewer_._circlePointEntities) viewer_._circlePointEntities = [];
      viewer_._circlePointEntities.push(pointEntity);
      setMeasurementPoints(points);
    }
  }, [viewer]);

  /**
   * Closes the current area measurement polygon
   * Called on right-click during area measurement
   */
  const closeAreaMeasurement = useCallback(() => {
    const viewer_ = viewer;
    const points = measurementPointsRef.current;
    
    if (points.length >= 3) {
      // Close the polygon by connecting last point to first
      const firstPoint = points[0];
      const lastPoint = points[points.length - 1];
      const closingLineEntity = viewer_.entities.add(createMeasureLine([lastPoint, firstPoint], Cesium.Color.RED));
      
      // Add filled polygon with red surface (30% opacity)
      const polygonEntity = viewer_.entities.add({
        polygon: {
          hierarchy: points,
          fill:true,
          perPositionHeight: true,
          material: Cesium.Color.RED.withAlpha(0.3),
          outline: false,
          height: 0
        }
      });
      
      // Calculate and display area
      const area = calculateSurfaceArea(points);
      const centroid = getCenterPoint(points);
      
      const labelEntity = viewer_.entities.add(createMeasureLabel(
        centroid,
        formatMeasurement(area, 'm²'),
        Cesium.Color.WHITE,
        Cesium.Color.BLACK
      ));
      
      // Collect all entities for this measurement
      const allEntities = [
        ...(viewer_._areaPointEntities || []),
        ...(viewer_._areaLineEntities || []),
        closingLineEntity,
        polygonEntity,
        labelEntity
      ];
      
      // Store completed measurement
      const measurementId = `area_${Date.now()}`;
      const completedMeasurement = {
        id: measurementId,
        type: 'area',
        value: area,
        unit: 'm²',
        displayText: formatMeasurement(area, 'm²'),
        entities: allEntities,
        visible: true,
        timestamp: new Date(),
        coordinates: {
          points: points.map(point => ({ x: point.x, y: point.y, z: point.z }))
        }
      };
      
      setCompletedMeasurements(prev => [...prev, completedMeasurement]);
      
      // Clear temporary storage
      viewer_._areaPointEntities = [];
      viewer_._areaLineEntities = [];
      
      // Reset points for next measurement but don't clear existing measurements
      setMeasurementPoints([]);
    }
  }, [viewer]);





  /**
   * Main click handler that delegates to appropriate measurement function
   * @param {Cesium.Cartesian3} position - Clicked position
   */
  const handleMeasurementClick = useCallback((position) => {
    const mode = measurementModeRef.current;
    
    switch (mode) {
      case MEASUREMENT_MODES.DISTANCE:
        handleDistanceMeasurement(position);
        break;
      case MEASUREMENT_MODES.HEIGHT:
        handleHeightMeasurement(position);
        break;
      case MEASUREMENT_MODES.ANGLE:
        handleAngleMeasurement(position);
        break;
      case MEASUREMENT_MODES.AREA:
        handleAreaMeasurement(position);
        break;
      case MEASUREMENT_MODES.CIRCLE:
        handleCircleMeasurement(position);
        break;
      default:
        break;
    }
  }, [handleDistanceMeasurement, handleHeightMeasurement, handleAngleMeasurement, handleAreaMeasurement, handleCircleMeasurement]);

  /**
   * Right-click handler for measurement modes
   * Closes polygon in area mode
   */
  const handleMeasurementRightClick = useCallback(() => {
    const mode = measurementModeRef.current;
    
    if (mode === MEASUREMENT_MODES.AREA) {
      closeAreaMeasurement();
    }
  }, [closeAreaMeasurement]);

  /**
   * Highlights a measurement by changing its visual appearance
   * @param {string} measurementId - ID of the measurement to highlight
   */
  const highlightMeasurement = useCallback((measurementId) => {
    if (!viewer || !viewer.entities) {
      return;
    }

    // Reset all measurements to normal appearance first
    completedMeasurements.forEach(m => {
      const isThisMeasurement = m.id === measurementId;
      const shouldHighlight = isThisMeasurement && measurementId !== highlightedMeasurement;
      
      m.entities.forEach(entity => {
        if (entity && viewer.entities.contains(entity)) {
          if (shouldHighlight) {
            // Apply fluorescent red highlighting
            if (entity.polyline) {
              entity.polyline.material = Cesium.Color.ORANGERED;
              entity.polyline.width = 4; // +1 width
            }
            if (entity.polygon) {
              if (entity.polygon.outlineColor) {
                entity.polygon.outlineColor = Cesium.Color.ORANGERED;
                entity.polygon.outlineWidth = 4; // +1 width
              }
              entity.polygon.material = Cesium.Color.ORANGERED.withAlpha(0.5); // More opaque
            }
            if (entity.point) {
              entity.point.color = Cesium.Color.ORANGERED;
              entity.point.pixelSize = 10; // Larger points
            }
            if (entity.cylinder) {
              entity.cylinder.material = m.type === 'Forest report' ? TREE_HIGHLIGHT_COLOR : Cesium.Color.ORANGERED;
            }
          } else {
            // Reset to default colors
            if (entity.polyline) {
              entity.polyline.material = Cesium.Color.RED;
              entity.polyline.width = 2;
            }
            if (entity.polygon) {
              if (entity.polygon.outlineColor) {
                entity.polygon.outlineColor = Cesium.Color.RED;
                entity.polygon.outlineWidth = 2;
              }
              entity.polygon.material = Cesium.Color.RED.withAlpha(0.3);
            }
            if (entity.point) {
              entity.point.color = Cesium.Color.RED;
              entity.point.pixelSize = 8;
            }
            if (entity.cylinder) {
              entity.cylinder.material = m.type === 'Forest report' ? TREE_COLOR : Cesium.Color.RED.withAlpha(0.7);
            }
          }
        }
      });
    });

    // Update highlighted measurement state
    if (measurementId === highlightedMeasurement) {
      setHighlightedMeasurement(null); // Toggle off
    } else {
      setHighlightedMeasurement(measurementId); // Toggle on
    }
  }, [viewer, completedMeasurements, highlightedMeasurement]);

  /**
   * Clears highlighting from all measurements
   */
  const clearMeasurementHighlight = useCallback(() => {
    highlightMeasurement(null);
  }, [highlightMeasurement]);

  /**
   * Toggles visibility of a measurement by showing/hiding its entities
   * @param {string} measurementId - ID of the measurement to toggle
   */
  const toggleMeasurementVisibility = useCallback((measurementId) => {
    if (!viewer || !viewer.entities) {
      return;
    }

    const measurement = completedMeasurements.find(m => m.id === measurementId);
    if (!measurement) return;

    // Determine current visibility state (check first entity)
    const firstEntity = measurement.entities[0];
    const isCurrentlyVisible = firstEntity && viewer.entities.contains(firstEntity) && firstEntity.show !== false;

    // Toggle visibility for all entities in this measurement
    measurement.entities.forEach(entity => {
      if (entity && viewer.entities.contains(entity)) {
        entity.show = !isCurrentlyVisible;
      }
    });

    // Update the measurement object to track visibility state
    setCompletedMeasurements(prev => 
      prev.map(m => 
        m.id === measurementId 
          ? { ...m, visible: !isCurrentlyVisible }
          : m
      )
    );
  }, [viewer, completedMeasurements]);

  return {
    measurementMode,
    measurementPoints,
    completedMeasurements,
    highlightedMeasurement,
    setMode,
    clearMeasurements,
    deleteMeasurement,
    exportMeasurements,
    importMeasurements,
    handleMeasurementClick,
    handleMeasurementRightClick,
    closeAreaMeasurement,
    highlightMeasurement,
    clearMeasurementHighlight,
    toggleMeasurementVisibility,
    MEASUREMENT_MODES
  };
};
